import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  addDoc,
  onSnapshot,
  query,
  where,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';

/**
 * Positive-framing time tracking for the /tasks page.
 *
 * A user "starts their day", tracks focus time on individual tasks, then "ends
 * their day". Data lives under the user's own document so it's private by
 * default:
 *   - users/{uid}/workDays/{yyyy-MM-dd}     -> the day's open/closed state
 *   - users/{uid}/timeEntries/{entryId}     -> individual focus sessions
 */

export interface WorkDay {
  day: string; // yyyy-MM-dd
  status: 'open' | 'closed';
  startedAt: Timestamp | null;
  endedAt: Timestamp | null;
  updatedAt?: Timestamp;
}

export interface TimeEntry {
  id: string;
  taskId: string | null;
  taskTitle: string;
  day: string; // yyyy-MM-dd
  startedAt: Timestamp;
  endedAt: Timestamp | null;
  durationSeconds: number; // filled in on stop
}

function workDaysCol(uid: string) {
  return collection(db, 'users', uid, 'workDays');
}
function timeEntriesCol(uid: string) {
  return collection(db, 'users', uid, 'timeEntries');
}

/** Subscribe to a single day's work-day doc. */
export function subscribeWorkDay(
  uid: string,
  day: string,
  cb: (workDay: WorkDay | null) => void
) {
  return onSnapshot(
    doc(workDaysCol(uid), day),
    (snap) => {
      if (!snap.exists()) {
        cb(null);
        return;
      }
      cb({ day, ...(snap.data() as any) } as WorkDay);
    },
    (err) => {
      console.error('subscribeWorkDay error:', err);
      cb(null);
    }
  );
}

/** Start (or re-open) the user's day. */
export async function startWorkDay(uid: string, day: string): Promise<void> {
  const ref = doc(workDaysCol(uid), day);
  const existing = await getDoc(ref);
  await setDoc(
    ref,
    {
      day,
      status: 'open',
      // Preserve the original start time if re-opening the same day.
      startedAt: existing.exists() && existing.data()?.startedAt
        ? existing.data()?.startedAt
        : Timestamp.now(),
      endedAt: null,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/** End the user's day (also stops any running entry — caller should stop first). */
export async function endWorkDay(uid: string, day: string): Promise<void> {
  await setDoc(
    doc(workDaysCol(uid), day),
    {
      day,
      status: 'closed',
      endedAt: Timestamp.now(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/** Subscribe to the currently-running entry (endedAt == null), if any. */
export function subscribeActiveEntry(
  uid: string,
  cb: (entry: TimeEntry | null) => void
) {
  const q = query(timeEntriesCol(uid), where('endedAt', '==', null));
  return onSnapshot(
    q,
    (snap) => {
      const open = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as any) }) as TimeEntry)
        .filter((e) => e.startedAt)
        .sort((a, b) => (b.startedAt?.toMillis?.() || 0) - (a.startedAt?.toMillis?.() || 0));
      cb(open[0] || null);
    },
    (err) => {
      console.error('subscribeActiveEntry error:', err);
      cb(null);
    }
  );
}

/** Subscribe to all entries for a given day (sorted newest-first client side). */
export function subscribeEntriesForDay(
  uid: string,
  day: string,
  cb: (entries: TimeEntry[]) => void
) {
  const q = query(timeEntriesCol(uid), where('day', '==', day));
  return onSnapshot(
    q,
    (snap) => {
      const entries = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as any) }) as TimeEntry)
        .sort((a, b) => (b.startedAt?.toMillis?.() || 0) - (a.startedAt?.toMillis?.() || 0));
      cb(entries);
    },
    (err) => {
      console.error('subscribeEntriesForDay error:', err);
      cb([]);
    }
  );
}

/**
 * Start tracking a task. Any currently-running entry is stopped first so there
 * is only ever one active timer.
 */
export async function startTimeEntry(
  uid: string,
  opts: { taskId: string | null; taskTitle: string; day: string }
): Promise<string> {
  await stopActiveEntries(uid);
  const ref = await addDoc(timeEntriesCol(uid), {
    taskId: opts.taskId ?? null,
    taskTitle: opts.taskTitle || 'Untitled task',
    day: opts.day,
    startedAt: Timestamp.now(),
    endedAt: null,
    durationSeconds: 0,
  });
  return ref.id;
}

/** Stop a specific entry, computing its duration. */
export async function stopTimeEntry(uid: string, entryId: string): Promise<void> {
  const ref = doc(timeEntriesCol(uid), entryId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data() as any;
  if (data.endedAt) return; // already stopped
  const startMs = data.startedAt?.toMillis?.() || Date.now();
  const now = Timestamp.now();
  const durationSeconds = Math.max(0, Math.round((now.toMillis() - startMs) / 1000));
  await updateDoc(ref, { endedAt: now, durationSeconds });
}

/** Stop every currently-running entry (safety when starting a new one). */
export async function stopActiveEntries(uid: string): Promise<void> {
  const { getDocs } = await import('firebase/firestore');
  const q = query(timeEntriesCol(uid), where('endedAt', '==', null));
  const results = await getDocs(q);
  await Promise.all(
    results.docs.map((d) => {
      const data = d.data() as any;
      if (data.endedAt) return Promise.resolve();
      const startMs = data.startedAt?.toMillis?.() || Date.now();
      const now = Timestamp.now();
      const durationSeconds = Math.max(0, Math.round((now.toMillis() - startMs) / 1000));
      return updateDoc(d.ref, { endedAt: now, durationSeconds });
    })
  );
}

/**
 * One-shot fetch of a user's focus history over an inclusive day range
 * (yyyy-MM-dd strings). Returns the day docs plus every time entry in range.
 */
export async function getFocusHistory(
  uid: string,
  startDay: string,
  endDay: string
): Promise<{ workDays: WorkDay[]; entries: TimeEntry[] }> {
  const { getDocs } = await import('firebase/firestore');

  const daysQ = query(
    workDaysCol(uid),
    where('day', '>=', startDay),
    where('day', '<=', endDay)
  );
  const entriesQ = query(
    timeEntriesCol(uid),
    where('day', '>=', startDay),
    where('day', '<=', endDay)
  );

  const [daysSnap, entriesSnap] = await Promise.all([getDocs(daysQ), getDocs(entriesQ)]);

  const workDays = daysSnap.docs.map((d) => ({ day: d.id, ...(d.data() as any) }) as WorkDay);
  const entries = entriesSnap.docs
    .map((d) => ({ id: d.id, ...(d.data() as any) }) as TimeEntry)
    .sort((a, b) => (a.startedAt?.toMillis?.() || 0) - (b.startedAt?.toMillis?.() || 0));

  return { workDays, entries };
}

/** Format seconds as H:MM:SS (or M:SS under an hour). */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}
